import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Loader2, Download, AlertTriangle } from "lucide-react";
import { toISODate, todayISO, currentMonthISO } from "@/lib/dateFormat";

// La fecha se arma en hora local: con toISOString() un reporte pedido de noche
// en Costa Rica saltaba al día siguiente.
const iso = (d) => toISODate(d);

// Rangos rápidos: semanal y mensual, que es lo que se mira para juzgar el
// comportamiento, más un rango libre para casos puntuales.
const PRESETS = {
  this_week: { label: "Esta semana", calc: () => {
    const hoy = todayISO();                 // día de Costa Rica
    const base = new Date(`${hoy}T12:00:00`);
    const day = (base.getDay() + 6) % 7;    // lunes = 0
    const from = new Date(base); from.setDate(base.getDate() - day);
    return [iso(from), hoy];
  } },
  this_month: { label: "Este mes", calc: () => [`${currentMonthISO()}-01`, todayISO()] },
  last_8_weeks: { label: "Últimas 8 semanas", calc: () => {
    const base = new Date(`${todayISO()}T12:00:00`);
    const from = new Date(base); from.setDate(base.getDate() - 56);
    return [iso(from), todayISO()];
  } },
  last_6_months: { label: "Últimos 6 meses", calc: () => {
    const base = new Date(`${todayISO()}T12:00:00`);
    const from = new Date(base.getFullYear(), base.getMonth() - 5, 15);
    return [`${iso(from).slice(0, 7)}-01`, todayISO()];
  } },
};

function Stat({ label, value, tone = "slate", hint }) {
  const tones = {
    slate: "text-slate-900", emerald: "text-emerald-700",
    amber: "text-amber-700", rose: "text-rose-700",
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
      <p className="text-[11px] uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`text-2xl font-bold ${tones[tone]}`}>{value}</p>
      {hint && <p className="text-[11px] text-slate-400 mt-0.5">{hint}</p>}
    </div>
  );
}

export default function AbsenceReport() {
  const [preset, setPreset] = useState("this_month");
  const [range, setRange] = useState(PRESETS.this_month.calc());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async ([from, to]) => {
    setLoading(true);
    try {
      const r = await api.get("/absence-requests/report", { params: { date_from: from, date_to: to } });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar el reporte");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(range); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (key) => {
    const r = PRESETS[key].calc();
    setPreset(key); setRange(r); load(r);
  };

  const rows = data?.rows || [];
  const totals = data?.totals;

  const csv = useMemo(() => {
    const head = ["Estudiante", "Grupo", "Programa", "Profesor", "Justificadas",
      "Por cuota", "Aprobadas por direccion", "A mano (sin solicitud)", "Marcadas por",
      "Pendientes", "Rechazadas", "Ciclo", "Cuota del ciclo", "Cuota usada", "Se paso", "Fechas"];
    const lines = rows.map((r) => [
      r.student_name, r.group_name, r.program_name, r.teacher_name, r.justified_total,
      r.via_quota, r.via_approved, r.via_manual, (r.manual_by || []).join(" / "),
      r.pending, r.rejected, r.cycle_index ?? "", r.cycle_quota ?? "", r.quota_used ?? "",
      r.exceeded ? "SI" : "no", (r.dates || []).join(" "),
    ]);
    return [head, ...lines]
      .map((l) => l.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
  }, [rows]);

  const download = () => {
    const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `ausencias_${range[0]}_${range[1]}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="space-y-4" data-testid="absence-report">
      <div className="flex flex-wrap items-end gap-2">
        {Object.entries(PRESETS).map(([key, p]) => (
          <Button key={key} size="sm" variant={preset === key ? "default" : "outline"}
                  onClick={() => pick(key)} data-testid={`absence-report-preset-${key}`}>
            {p.label}
          </Button>
        ))}
        <div className="flex items-end gap-2 ml-auto">
          <div>
            <Label className="text-[11px] text-slate-500">Desde</Label>
            <Input type="date" value={range[0]} className="h-9 w-[145px]"
                   data-testid="absence-report-from"
                   onChange={(e) => { setPreset("custom"); setRange([e.target.value, range[1]]); }} />
          </div>
          <div>
            <Label className="text-[11px] text-slate-500">Hasta</Label>
            <Input type="date" value={range[1]} className="h-9 w-[145px]"
                   data-testid="absence-report-to"
                   onChange={(e) => { setPreset("custom"); setRange([range[0], e.target.value]); }} />
          </div>
          <Button size="sm" variant="secondary" onClick={() => load(range)}
                  data-testid="absence-report-apply">Aplicar</Button>
          <Button size="sm" variant="outline" onClick={download} disabled={!rows.length}
                  data-testid="absence-report-csv">
            <Download className="h-3.5 w-3.5 mr-1.5" /> CSV
          </Button>
        </div>
      </div>

      {loading && (
        <div className="p-10 flex items-center justify-center text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Cargando…
        </div>
      )}

      {!loading && totals && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat label="Justificadas" value={totals.justified} />
            <Stat label="Por cuota" value={totals.via_quota} tone="emerald"
                  hint="pedidas a tiempo, con cuota" />
            <Stat label="Aprobadas" value={totals.via_approved} tone="emerald"
                  hint="visto bueno de dirección" />
            <Stat label="A mano" value={totals.via_manual} tone="amber"
                  hint="sin solicitud, sin pasar por la cuota" />
            <Stat label="Pendientes" value={totals.pending} tone="rose"
                  hint={`${totals.rejected} rechazadas`} />
          </div>

          <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Estudiante</TableHead>
                  <TableHead>Grupo</TableHead>
                  <TableHead className="text-center">Justificadas</TableHead>
                  <TableHead className="text-center">Por cuota</TableHead>
                  <TableHead className="text-center">Aprobadas</TableHead>
                  <TableHead className="text-center">A mano</TableHead>
                  <TableHead className="text-center">Pendientes</TableHead>
                  <TableHead className="text-center">Cuota del ciclo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-sm text-slate-500 py-8">
                      No hay justificadas en este período.
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((r) => (
                  <TableRow key={`${r.student_id}-${r.group_id}`}
                            data-testid={`absence-report-row-${r.student_id}`}>
                    <TableCell>
                      <p className="font-medium text-slate-800">{r.student_name}</p>
                      <p className="text-[11px] text-slate-400">{(r.dates || []).join(" · ")}</p>
                    </TableCell>
                    <TableCell className="text-xs text-slate-600">
                      {r.group_name}
                      {r.teacher_name && <span className="text-slate-400"> · {r.teacher_name}</span>}
                    </TableCell>
                    <TableCell className="text-center font-semibold">{r.justified_total}</TableCell>
                    <TableCell className="text-center text-emerald-700">{r.via_quota || "—"}</TableCell>
                    <TableCell className="text-center text-emerald-700">{r.via_approved || "—"}</TableCell>
                    <TableCell className="text-center">
                      {r.via_manual ? (
                        <span className="text-amber-700 font-semibold" title={(r.manual_by || []).join(", ")}>
                          {r.via_manual}
                        </span>
                      ) : "—"}
                    </TableCell>
                    <TableCell className="text-center text-rose-700">{r.pending || "—"}</TableCell>
                    <TableCell className="text-center">
                      {r.cycle_quota === null ? (
                        <span className="text-slate-400 text-xs">—</span>
                      ) : r.unlimited ? (
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                          Sin límite
                        </Badge>
                      ) : (
                        <Badge variant="outline"
                               className={r.exceeded
                                 ? "bg-rose-50 text-rose-700 border-rose-200 gap-1"
                                 : "bg-slate-50 text-slate-600 border-slate-200"}>
                          {r.exceeded && <AlertTriangle className="h-3 w-3" />}
                          {r.quota_used}/{r.cycle_quota}
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <p className="text-[11px] text-slate-500">
            «A mano» son las justificadas marcadas en la hoja de asistencia sin solicitud del
            estudiante: no pasan por la cuota del 25 % ni dejan motivo. La columna «Cuota del ciclo»
            es la del ciclo que está corriendo hoy, no la del período.
          </p>
        </>
      )}
    </div>
  );
}
